import { useTableFilters } from "@lib/hooks/useLocalStorage";
import { useCallback, useMemo, useState } from "react";

export interface TicketsFilters {
  activeQuickFilter: string;
  setActiveQuickFilter: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isDevView: boolean;
  setIsDevView: (value: boolean) => void;
  selectedStatus: string;
  setSelectedStatus: (value: string) => void;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
  apiFilters: Record<string, unknown>;
  clearFilters: () => void;
}

const QUICK_FILTERS = [
  { id: "all", filter: undefined },
  { id: "open", filter: { status: "open" } },
  { id: "proposition_solution", filter: { status: "proposition_solution" } },
  { id: "bug", filter: { category: "bug_bloquant" } },
] as const;

/**
 * Centralizes all ticket list filter state (quick filter, status, category,
 * search, dev view) with persistence via useTableFilters.
 */
export function useTicketsFilters(): TicketsFilters {
  const tableId = "tickets";
  const {
    filters: persistedFilters,
    updateFilter,
    clearFilters: clearPersistedFilters,
  } = useTableFilters(tableId);

  const [activeQuickFilter, setActiveQuickFilter] = useState(
    ((persistedFilters as unknown as Record<string, unknown>).activeFilter as string) || "all",
  );
  const [searchQuery, setSearchQuery] = useState(persistedFilters.globalFilter || "");
  const [isDevView, setIsDevView] = useState(
    (persistedFilters as unknown as Record<string, unknown>).isDevView === true,
  );
  const [selectedStatus, setSelectedStatus] = useState<string>(
    (persistedFilters.columnFilters?.status as string) || "",
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    (persistedFilters.columnFilters?.category as string) || "",
  );

  const updateActiveQuickFilter = useCallback(
    (value: string) => {
      setActiveQuickFilter(value);
      updateFilter("activeFilter" as never, value as never);
    },
    [updateFilter],
  );

  const updateIsDevView = useCallback(
    (value: boolean) => {
      setIsDevView(value);
      updateFilter("isDevView" as never, value as never);
    },
    [updateFilter],
  );

  const updateSearchQuery = useCallback(
    (value: string) => {
      setSearchQuery(value);
      updateFilter("globalFilter", value);
    },
    [updateFilter],
  );

  const updateSelectedStatus = useCallback(
    (value: string) => {
      setSelectedStatus(value);
      updateFilter("columnFilters", { ...persistedFilters.columnFilters, status: value });
    },
    [updateFilter, persistedFilters.columnFilters],
  );

  const updateSelectedCategory = useCallback(
    (value: string) => {
      setSelectedCategory(value);
      updateFilter("columnFilters", { ...persistedFilters.columnFilters, category: value });
    },
    [updateFilter, persistedFilters.columnFilters],
  );

  const apiFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};
    if (activeQuickFilter !== "all") {
      const qf = QUICK_FILTERS.find((f) => f.id === activeQuickFilter);
      if (qf?.filter) Object.assign(filters, qf.filter);
    }
    if (selectedStatus) filters.status = selectedStatus;
    if (selectedCategory) filters.category = selectedCategory;
    if (!isDevView) filters.creatorIsCurrentUser = true;
    return filters;
  }, [activeQuickFilter, selectedStatus, selectedCategory, isDevView]);

  const clearFilters = useCallback(() => {
    setActiveQuickFilter("all");
    setIsDevView(false);
    setSearchQuery("");
    setSelectedStatus("");
    setSelectedCategory("");
    clearPersistedFilters();
  }, [clearPersistedFilters]);

  return {
    activeQuickFilter,
    setActiveQuickFilter: updateActiveQuickFilter,
    searchQuery,
    setSearchQuery: updateSearchQuery,
    isDevView,
    setIsDevView: updateIsDevView,
    selectedStatus,
    setSelectedStatus: updateSelectedStatus,
    selectedCategory,
    setSelectedCategory: updateSelectedCategory,
    apiFilters,
    clearFilters,
  };
}
